import { getResolvedCart, isFullCart, mergeAddIntoLive, resolveCart, setResolvedCart } from './component-cart-hydrate.js';

/**
 * Cart Free Samples.
 *
 * Mirrors JonesRoadBeauty-Main 8/app/components/cart/cartSamples.js, adapted
 * for this theme's liquid-ajax-cart pipeline. Renders a "Pick N Free Sample(s)"
 * block inside the cart drawer, with a bottom-sheet shade picker for
 * multi-variant samples. Configured via window.settings (cart_samples_config,
 * cart_samples_limit, region-specific cart_samples_enable_* flags).
 *
 * Auto-runs on:
 *   - DOMContentLoaded
 *   - liquid-ajax-cart:request-end (after cart add/update/remove)
 *   - cart-open (when the drawer is opened)
 */

const _samplePageCache = new Map();
const _sampleFetchInFlight = new Map();

const PLUS_SVG = '<svg viewBox="0 0 13 13" aria-hidden="true" focusable="false"><path d="M6.5 2v9M2 6.5h9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
const CHECK_SVG = '<svg viewBox="0 0 13 13" aria-hidden="true" focusable="false"><path d="M2.5 6.7l2.5 2.5 5.5-5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

const cartSamples = (() => {
  const EL_SAMPLES = '.js-cartSamples';

  const configured = (window.settings && window.settings.cart_samples_config) || [];
  const legacyHandles = (window.settings && window.settings.cart_samples) || [];
  const SAMPLES = configured.length
    ? configured
    : legacyHandles.length
      ? legacyHandles.map((h) => ({ handle: h, mode: 'auto', variant_default: '' }))
      : [];
  const LIMIT = (window.settings && window.settings.cart_samples_limit) || 1;

  if (!SAMPLES.length) {
    return {
      init: () => {},
      closeShadePicker: () => {},
      removeOrphanSamples: () => false,
      interceptRemovalClick: () => false,
    };
  }

  const CURRENCY_SUFFIX_MAP = {
    USD: '',
    CAD: '_cad',
    GBP: '_gbp',
    EUR: '_eur',
    AUD: '_aud',
    NZD: '_nzd',
    BGN: '_bgn',
    CZK: '_czk',
    DKK: '_dkk',
    HUF: '_huf',
    PLN: '_pln',
    RON: '_ron',
    SEK: '_sek',
  };

  let _shadePicker = null;

  function isEnabledForCurrentRegion() {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const suffix = CURRENCY_SUFFIX_MAP[currency];
    if (suffix === undefined) return false;
    return !!(window.settings && window.settings['cart_samples_enable' + suffix]);
  }

  function isSampleLineItem(item) {
    const p = item.properties;
    if (!p) return false;
    const v = p._free_sample;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  function getSampleCartItems(cartItems) {
    return cartItems.filter(isSampleLineItem);
  }

  function getSampleTotalQty(cartItems) {
    return getSampleCartItems(cartItems).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  function hasQualifyingCartItems(cartItems) {
    return (cartItems || []).some((item) => !isSampleLineItem(item));
  }

  let _samplesRemovalInFlight = false;

  function removeOrphanSamples(cartItems) {
    if (_samplesRemovalInFlight || !Array.isArray(cartItems)) return false;

    const sampleItems = getSampleCartItems(cartItems);
    if (!sampleItems.length || hasQualifyingCartItems(cartItems)) return false;

    const updates = {};
    sampleItems.forEach((item) => {
      updates[item.key] = 0;
    });

    _samplesRemovalInFlight = true;

    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      window.liquidAjaxCart.update(
        { updates },
        {
          info: { initiator: 'cart-samples' },
          lastCallback: () => {
            _samplesRemovalInFlight = false;
          },
        }
      );
    } else {
      window
        .fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        })
        .finally(() => {
          _samplesRemovalInFlight = false;
        });
    }

    return true;
  }

  function getLiveCart() {
    const live = window.liquidAjaxCart && window.liquidAjaxCart.cart;
    if (isFullCart(live)) return live;
    const resolved = getResolvedCart();
    if (isFullCart(resolved)) return resolved;
    return null;
  }

  function resolveItemFromRemoveHref(cartItems, href, lineEl) {
    let parsed;
    try {
      parsed = new URL(href, window.location.origin);
    } catch (e) {
      parsed = null;
    }

    if (parsed && /\/cart\/change/.test(parsed.pathname)) {
      const quantity = Number(parsed.searchParams.get('quantity') ?? 0);
      if (quantity !== 0) return null;

      const line = parsed.searchParams.get('line');
      const id = parsed.searchParams.get('id');

      if (line != null) {
        const idx = Number(line) - 1;
        if (cartItems[idx]) return cartItems[idx];
      }
      if (id != null) {
        const match = cartItems.find(
          (item) => item.key === id || String(item.variant_id) === id
        );
        if (match) return match;
      }
    }

    if (lineEl) {
      const key = lineEl.getAttribute('data-line-item-key');
      if (key) {
        return cartItems.find((item) => item.key === key) || null;
      }
    }

    return null;
  }

  function buildBatchRemovalUpdates(cartItems, targetItem) {
    if (!targetItem || isSampleLineItem(targetItem)) return null;

    const sampleItems = getSampleCartItems(cartItems);
    if (!sampleItems.length) return null;

    const remainingQualifying = cartItems.filter(
      (item) => item.key !== targetItem.key && !isSampleLineItem(item)
    );
    if (remainingQualifying.length > 0) return null;

    const updates = { [targetItem.key]: 0 };
    sampleItems.forEach((item) => {
      updates[item.key] = 0;
    });
    return updates;
  }

  function interceptRemovalClick(event) {
    const reqBtn = event.target.closest('[data-ajax-cart-request-button]');
    if (!reqBtn || !reqBtn.closest('cart-drawer')) return false;
    if (reqBtn.closest('.cart-item--free-sample')) return false;
    if (window.liquidAjaxCart && window.liquidAjaxCart.processing) return false;

    const href = reqBtn.getAttribute('href');
    if (!href) return false;

    const cart = getLiveCart();
    if (!cart) return false;

    const lineEl = reqBtn.closest('[data-line-item-key]');
    const targetItem = resolveItemFromRemoveHref(cart.items, href, lineEl);
    const updates = buildBatchRemovalUpdates(cart.items, targetItem);
    if (!updates) return false;

    event.preventDefault();
    event.stopImmediatePropagation();

    _samplesRemovalInFlight = true;

    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      window.liquidAjaxCart.update(
        { updates },
        {
          info: { initiator: 'cart-samples' },
          lastCallback: () => {
            _samplesRemovalInFlight = false;
          },
        }
      );
    } else {
      window
        .fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        })
        .finally(() => {
          _samplesRemovalInFlight = false;
        });
    }

    return true;
  }

  function resolveVariantByName(product, shadeName) {
    if (!shadeName || !product.variants.length) return null;
    const target = shadeName.trim().toLowerCase();
    return product.variants.find((v) => {
      const fields = [v.title, v.option1, v.option2, v.option3]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());
      return fields.some((f) => f === target) || fields.some((f) => f.includes(target));
    }) || null;
  }

  function getCardData(product, config) {
    const hasMultiple = product.variants.length > 1;
    const mode = config.mode || 'auto';

    if (!hasMultiple) {
      return { needsPicker: false, forcedVariant: product.variants[0] };
    }
    if (mode === 'specific_shade') {
      const match = resolveVariantByName(product, config.variant_default);
      return { needsPicker: false, forcedVariant: match || product.variants[0] };
    }
    return { needsPicker: true, forcedVariant: null };
  }

  function findSelectedVariantForProduct(product, sampleItems) {
    const ids = new Set(product.variants.map((v) => v.id));
    const match = sampleItems.find((item) => ids.has(item.variant_id));
    if (!match) return null;
    return product.variants.find((v) => v.id === match.variant_id) || null;
  }

  function bind(cartItems) {
    const count = getSampleTotalQty(cartItems);
    const limitReached = count >= LIMIT;

    document.querySelectorAll('.js-sampleAdd').forEach((btn) => {
      btn.removeEventListener('click', handleAddClick);
      btn.addEventListener('click', handleAddClick);
      btn.disabled = limitReached;
    });

    document.querySelectorAll('.js-sampleRemove').forEach((btn) => {
      btn.removeEventListener('click', handleRemoveClick);
      btn.addEventListener('click', handleRemoveClick);
    });

    document.querySelectorAll('.js-samplePickerOpen').forEach((btn) => {
      btn.removeEventListener('click', handlePickerOpen);
      btn.addEventListener('click', handlePickerOpen);
      btn.disabled = limitReached;
    });
  }

  function renderSamplesBlock(cartItems) {
    const elSamples = document.querySelector(EL_SAMPLES);
    if (!elSamples || !elSamples._sampleProducts || !Array.isArray(cartItems)) return;
    elSamples.innerHTML = template(elSamples._sampleProducts, cartItems);
    bind(cartItems);
  }

  function refreshCartUI() {
    resolveCart(null)
      .then((cart) => renderSamplesBlock(cart.items || []))
      .catch(() => {});
  }

  function addVariantToCart(variantId) {
    const body = {
      id: Number(variantId),
      quantity: 1,
      properties: { _source: 'Cart Samples', _free_sample: true },
    };

    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.add === 'function') {
      return new Promise((resolve, reject) => {
        window.liquidAjaxCart.add(
          { items: [body] },
          {
            lastCallback: (requestState) => {
              if (requestState && requestState.responseData && requestState.responseData.ok) {
                resolve();
              } else {
                reject(new Error('Sample add failed'));
              }
            },
          }
        );
      });
    }

    return window
      .fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((b) => {
            const msg = (b && (b.description || b.message)) || 'Could not add sample';
            throw new Error(msg);
          });
        }
        refreshCartUI();
      });
  }

  function handleAddClick(ev) {
    const btn = ev.currentTarget;
    if (btn.disabled) return;
    document.querySelectorAll('.js-sampleAdd, .js-samplePickerOpen').forEach((b) => {
      b.disabled = true;
    });
    addVariantToCart(btn.dataset.id).catch(() => {
      refreshCartUI();
    });
  }

  function handleRemoveClick(ev) {
    const btn = ev.currentTarget;
    const key = btn.dataset.id;
    if (!key) return;

    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.change === 'function') {
      try {
        window.liquidAjaxCart.change({ id: key, quantity: 0 });
        return;
      } catch (e) {
        // fall through
      }
    }

    window.fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [key]: 0 } }),
    }).then(refreshCartUI);
  }

  function getShadePicker() {
    if (!_shadePicker) {
      _shadePicker = document.createElement('div');
      _shadePicker.className = 'cart-shade-picker cart-shade-picker--samples';
      _shadePicker.innerHTML = `
        <div class="cart-shade-picker__overlay js-sampleShadeOverlay"></div>
        <div class="cart-shade-picker__panel js-sampleShadePanel">
          <div class="cart-shade-picker__header">
            <span class="cart-shade-picker__title js-sampleShadeTitle">Select a Shade</span>
            <button type="button" class="cart-shade-picker__close js-sampleShadeClose" aria-label="Close">&#x2715;</button>
          </div>
          <div class="cart-shade-picker__list js-sampleShadeList"></div>
        </div>
      `;
      _shadePicker.querySelector('.js-sampleShadeOverlay').addEventListener('click', closeShadePicker);
      _shadePicker.querySelector('.js-sampleShadeClose').addEventListener('click', closeShadePicker);
      _shadePicker.hidden = true;
      document.body.appendChild(_shadePicker);
    }
    return _shadePicker;
  }

  function positionPicker(picker) {
    const panel = picker.querySelector('.js-sampleShadePanel');
    const cartPane = document.querySelector('cart-drawer .drawer__wrapper')
      || document.querySelector('.cart-drawer__pane');
    if (cartPane && panel) {
      const rect = cartPane.getBoundingClientRect();
      panel.style.left = rect.left + 'px';
      panel.style.width = rect.width + 'px';
    }
  }

  function closeShadePicker(opts) {
    const skipResync = opts && opts.skipResync;
    if (_shadePicker) {
      _shadePicker.classList.remove('open');
      _shadePicker.hidden = true;
      const list = _shadePicker.querySelector('.js-sampleShadeList');
      if (list) list.innerHTML = '';
    }
    if (!skipResync) {
      resolveCart(null)
        .then((cart) => renderSamplesBlock(cart.items || []))
        .catch(() => {});
    }
  }

  function handlePickerOpen(ev) {
    const btn = ev.currentTarget;
    if (btn.disabled) return;
    const card = btn.closest('.cart-sample-card');
    if (!card) return;
    const handle = card.dataset.handle;
    const elSamples = document.querySelector(EL_SAMPLES);
    const product = ((elSamples && elSamples._sampleProducts) || []).find((p) => p.handle === handle);
    if (!product) return;

    document.querySelectorAll('.js-sampleAdd, .js-samplePickerOpen').forEach((b) => {
      b.disabled = true;
    });

    const newShades = ((window.settings && window.settings.msu_new_shades) || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const picker = getShadePicker();
    picker.querySelector('.js-sampleShadeTitle').textContent = `Select a Shade — ${product.title}`;
    const list = picker.querySelector('.js-sampleShadeList');

    list.innerHTML = product.variants.map((v) => {
      // Coerce defensively — metafield objects accidentally leaking through
      // would otherwise stringify as "[object Object]" in the inline style.
      const rawBg = typeof v.swatch_bg === 'string' ? v.swatch_bg : '';
      const rawColor = typeof v.color === 'string' ? v.color : '';
      const swatchStyle = v.swatch_is_img && rawBg
        ? `background-image:url('${rawBg}');background-size:cover;background-position:center;`
        : `background:${rawBg || rawColor || '#ccc'};`;
      const isNew = newShades.includes(v.title);
      return `
        <div class="cart-shade-row" data-id="${v.id}">
          <span class="cart-shade-row__dot" style="${swatchStyle}"></span>
          <span class="cart-shade-row__text">
            <span class="cart-shade-row__name-wrap">
              <span class="cart-shade-row__name">${escapeHtml(v.title)}</span>
              ${isNew ? '<span class="cart-shade-row__new">NEW</span>' : ''}
            </span>
            ${v.description ? `<span class="cart-shade-row__desc">${escapeHtml(v.description)}</span>` : ''}
          </span>
          <span class="cart-shade-row__check">&#x2713;</span>
        </div>
      `;
    }).join('');

    list.querySelectorAll('.cart-shade-row').forEach((row) => {
      row.addEventListener('click', () => {
        const variantId = row.dataset.id;
        closeShadePicker({ skipResync: true });
        document.querySelectorAll('.js-sampleAdd, .js-samplePickerOpen').forEach((b) => {
          b.disabled = true;
        });
        resolveCart(null)
          .then((latest) => {
            if (getSampleTotalQty(latest.items) >= LIMIT) {
              renderSamplesBlock(latest.items);
              return;
            }
            return addVariantToCart(variantId);
          })
          .catch(() => {
            refreshCartUI();
          });
      });
    });

    list.scrollTop = 0;
    positionPicker(picker);
    picker.hidden = false;
    picker.classList.add('open');
    requestAnimationFrame(() => {
      list.scrollTop = 0;
    });
  }

  function parseSwatchList() {
    const raw = (window.settings && window.settings.swatches) || '';
    return String(raw)
      .split(/\r?\n/)
      .map((line) => {
        const parts = line.split('==');
        if (parts.length < 2) return null;
        return { name: parts[0].trim(), value: parts.slice(1).join('==').trim() };
      })
      .filter(Boolean);
  }

  function cleanVariantTitle(t) {
    if (!t) return '';
    let out = t;
    if (out.includes(' / ')) out = out.split(' / ')[0];
    else if (out.includes('-')) out = out.split('-')[0];
    else if (out.includes('(')) out = out.split('(')[0];
    return out.trim();
  }

  function normalizeProduct(handle, data, cfg) {
    const swatchesGlobal = parseSwatchList();

    const variants = (data.variants || []).map((v) => {
      const title = cleanVariantTitle(v.title);
      let swatch_bg = '';
      let swatch_is_img = false;

      // /products/{handle}.js does NOT expose variant.metafields.swatch.color or
      // .image. Restrict resolution to the global swatch_colors map; the
      // Liquid-rendered #cart-samples-data path handles the metafield case.
      const byId = swatchesGlobal.find((s) => String(s.name) === String(v.id));
      const byName = swatchesGlobal.find((s) => String(s.name) === title);
      const color = byId ? byId.value : byName ? byName.value : '';
      if (color) {
        swatch_bg = color;
        swatch_is_img = /\.(png|jpe?g|gif|webp|svg)/i.test(color) || /\/\//.test(color);
      }

      return {
        id: v.id,
        title,
        option1: v.option1 || null,
        option2: v.option2 || null,
        option3: v.option3 || null,
        swatch_bg,
        swatch_is_img,
        color: !swatch_is_img ? swatch_bg : '',
        description: '',
      };
    });

    const cleanProductTitle = (t) => {
      if (!t) return t;
      return String(t)
        .replace(/\s*[-–—:]\s*samples?\s*$/i, '')
        .replace(/\s*\(\s*samples?\s*\)\s*$/i, '')
        .replace(/\s+samples?\s*$/i, '')
        .trim();
    };

    const featured = data.featured_image || (data.images && data.images[0]) || '';
    return {
      handle,
      id: data.id,
      title: cleanProductTitle(data.title),
      url: data.url || `/products/${handle}`,
      image: featured,
      variants,
      _config: cfg,
    };
  }

  /**
   * Reads the Liquid-rendered sample data from #cart-samples-data. The
   * snippet `component-cart-samples-data.liquid` outputs each configured
   * sample product with variants that already include swatch_bg (resolved
   * from variant.metafields.swatch + the global swatch_colors map) and
   * findation_hero descriptions — data we can't get from /products/{h}.js.
   */
  function loadFromLiquid() {
    const el = document.getElementById('cart-samples-data');
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(data)) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function fetchSamples() {
    const liquidData = loadFromLiquid();
    if (liquidData && liquidData.length) {
      // Hydrate cache + attach matching _config (mode/variant_default) so the
      // shade-picker logic still respects 'auto' / 'customer_picks' / 'specific_shade'.
      const configByHandle = new Map(SAMPLES.map((c) => [c.handle, c]));
      const hydrated = liquidData
        .filter((p) => p && p.handle && configByHandle.has(p.handle))
        .map((p) => {
          const cfg = configByHandle.get(p.handle);
          const product = {
            handle: p.handle,
            id: p.id,
            title: p.title,
            url: p.url,
            image: p.image,
            variants: (p.variants || []).map((v) => ({
              id: v.id,
              title: v.title,
              option1: v.option1 || null,
              option2: v.option2 || null,
              option3: v.option3 || null,
              swatch_bg: v.swatch_bg || '',
              swatch_is_img: !!v.swatch_is_img,
              color: v.color || '',
              description: v.description || '',
            })),
            _config: cfg,
          };
          _samplePageCache.set(p.handle, product);
          return product;
        });
      if (hydrated.length) {
        return Promise.resolve(hydrated);
      }
    }

    return Promise.all(
      SAMPLES.map((cfg) => {
        const handle = cfg.handle;
        if (_samplePageCache.has(handle)) {
          return Promise.resolve({ ...(_samplePageCache.get(handle)), _config: cfg });
        }
        if (_sampleFetchInFlight.has(handle)) {
          return _sampleFetchInFlight.get(handle).then((p) => ({ ...p, _config: cfg }));
        }
        const promise = window
          .fetch(`/products/${handle}.js`)
          .then((res) => {
            if (!res.ok) throw new Error(`Could not load product ${handle}`);
            return res.json();
          })
          .then((data) => {
            const parsed = normalizeProduct(handle, data, cfg);
            _samplePageCache.set(handle, parsed);
            return parsed;
          })
          .finally(() => {
            _sampleFetchInFlight.delete(handle);
          });
        _sampleFetchInFlight.set(handle, promise);
        return promise.then((p) => ({ ...p, _config: cfg }));
      })
    );
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function variantDisplayName(v) {
    if (v.title && v.title !== 'Default Title') return v.title;
    return v.option1 || '';
  }

  function renderCard(product, sampleItems, limitReached) {
    const selectedVariant = findSelectedVariantForProduct(product, sampleItems);
    const isAdded = !!selectedVariant;
    const { needsPicker, forcedVariant } = getCardData(product, product._config || { mode: 'auto', variant_default: '' });

    const displayTitle = isAdded && selectedVariant && variantDisplayName(selectedVariant)
      ? `${product.title} — ${variantDisplayName(selectedVariant)}`
      : product.title;

    // For the in-cart line, look up the cart line key so removal targets
    // the exact line (Shopify accepts variant_id too, but key is safer).
    const matchedCartLine = isAdded
      ? sampleItems.find((item) => item.variant_id === selectedVariant.id)
      : null;
    const removeId = matchedCartLine ? matchedCartLine.key : (selectedVariant ? selectedVariant.id : '');

    let frontButton = '';
    if (isAdded) {
      frontButton = `<button type="button" class="cart-sample-card__icon js-sampleRemove" data-id="${removeId}" aria-label="Remove ${escapeHtml(product.title)}">${CHECK_SVG}</button>`;
    } else if (needsPicker) {
      frontButton = `<button type="button" class="cart-sample-card__icon js-samplePickerOpen" aria-label="Choose shade for ${escapeHtml(product.title)}" ${limitReached ? 'disabled' : ''}>${PLUS_SVG}</button>`;
    } else if (forcedVariant) {
      const variantId = forcedVariant.id;
      frontButton = `<button type="button" class="cart-sample-card__icon js-sampleAdd" data-id="${variantId}" aria-label="Add ${escapeHtml(product.title)}" ${limitReached ? 'disabled' : ''}>${PLUS_SVG}</button>`;
    }

    return `
      <div class="cart-sample-card${isAdded ? ' is-selected' : ''}" data-handle="${product.handle}">
        <div class="cart-sample-card__img-wrap">
          <img src="${product.image}" alt="${escapeHtml(product.title)}" class="cart-sample-card__img" />
          <span class="cart-sample-card__badge">FREE</span>
        </div>
        <div class="cart-sample-card__body">
          <p class="cart-sample-card__name">${escapeHtml(displayTitle)}</p>
          ${frontButton}
        </div>
      </div>
    `;
  }

  function template(samples, cartItems) {
    const sampleItems = getSampleCartItems(cartItems);
    const count = getSampleTotalQty(cartItems);
    const limitReached = count >= LIMIT;
    const label = LIMIT === 1 ? 'Pick 1 Free Sample' : `Pick ${LIMIT} Free Samples`;

    return `
      <div class="cart-samples__header">
        <span class="cart-samples__heading">${label}</span>
        <span class="cart-samples__counter js-samplesCounter">${count}/${LIMIT}</span>
      </div>
      <div class="cart-samples__grid">
        ${samples.map((product) => renderCard(product, sampleItems, limitReached)).join('')}
      </div>
    `;
  }

  function init(cartItems) {
    const elSamples = document.querySelector(EL_SAMPLES);
    if (!elSamples) return;

    if (!isEnabledForCurrentRegion()) {
      elSamples.innerHTML = '';
      return;
    }

    if (!hasQualifyingCartItems(cartItems)) {
      elSamples.innerHTML = '';
      return;
    }

    if (elSamples._sampleProducts) {
      elSamples.innerHTML = template(elSamples._sampleProducts, cartItems);
      bind(cartItems);
      return;
    }

    fetchSamples()
      .then((samples) => {
        elSamples._sampleProducts = samples;
        elSamples.innerHTML = template(samples, cartItems);
        bind(cartItems);
      })
      .catch((err) => {
        console.error('[cartSamples] fetch failed:', err);
        elSamples.innerHTML = '';
      });
  }

  return { init, closeShadePicker: () => closeShadePicker({ skipResync: true }), removeOrphanSamples, interceptRemovalClick };
})();

let _samplesInitPending = null;

function scheduleInit(cart) {
  if (isFullCart(cart)) {
    if (cartSamples.removeOrphanSamples(cart.items || [])) return;
    cartSamples.init(cart.items || []);
    return;
  }

  if (_samplesInitPending) return;

  _samplesInitPending = resolveCart(cart)
    .then((c) => {
      if (cartSamples.removeOrphanSamples(c.items || [])) return;
      cartSamples.init(c.items || []);
    })
    .catch(() => {})
    .finally(() => {
      _samplesInitPending = null;
    });
}

if (typeof window !== 'undefined') {
  window.cartSamples = cartSamples;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
} else {
  scheduleInit();
}

let _samplesHandledAt = 0;

document.addEventListener('liquid-ajax-cart:request-end', (event) => {
  const rs = event.detail && event.detail.requestState;
  const rd = rs && rs.responseData;
  if (!rd || !rd.ok) return;
  if (rs.requestType === 'get') return;
  _samplesHandledAt = Date.now();

  const cart = event.detail?.cart;
  const count = cart?.item_count ?? rd.body?.item_count;
  if (count === 0) cartSamples.closeShadePicker();

  const items = isFullCart(cart)
    ? cart.items
    : isFullCart(rd.body)
      ? rd.body.items
      : null;

  if (items && rs.info?.initiator !== 'cart-samples' && cartSamples.removeOrphanSamples(items)) {
    return;
  }

  if (rs.requestType === 'add' && rd.body) {
    const merged = mergeAddIntoLive(rd.body);
    if (merged) {
      setResolvedCart(merged);
      cartSamples.init(merged.items || []);
      return;
    }
  }

  scheduleInit(rd.body);
});

window.addEventListener('cart-open', () => {
  if (Date.now() - _samplesHandledAt < 400) return;
  scheduleInit();
});

window.addEventListener('cart-close', () => {
  cartSamples.closeShadePicker();
});

document.addEventListener(
  'click',
  (event) => {
    cartSamples.interceptRemovalClick(event);
  },
  true
);

export default cartSamples;
